import { Skeleton, Stack, Typography } from '@mui/material';
import { fromNullToUndefined, isError, isNotEmptyArray, isNotNullOrUndefined, vyDate } from '@vy/utils';
import React, { useState } from 'react';
import { VyDivider } from '../../../../../../../components/new-design/atoms';
import { VyNote } from '../../../../../../../components/new-design/data-display/VyNote/VyNote';
import { VyPaper } from '../../../../../../../components/new-design/data-display/VyPaper/VyPaper';
import { VyDialog } from '../../../../../../../components/new-design/feedback/VyDialog';
import { useDialog } from '../../../../../../../hooks/use-dialog';
import { getUserId } from '../../../../../../../services/user-auth';
import { formatDate } from '../../../../../../../utils/date';
import { userMessage } from '../../../../../../../utils/vy-toasts';
import { useCreateCustomerNote } from '../../customer-overview-hooks/use-create-customer-note';
import { useCustomerNotes } from '../../customer-overview-hooks/use-customer-notes';
import { useModifyCustomerNote } from '../../customer-overview-hooks/use-modify-customer-note';
import { useRemoveCustomerNote } from '../../customer-overview-hooks/use-remove-customer-note';
import { CreateCustomerNoteForm } from './CreateCustomerNoteForm';

type CustomerNotesProps = {
  customerId: string;
};

export const CustomerNotes: React.FC<CustomerNotesProps> = ({ customerId }) => {
  const { data, isLoading } = useCustomerNotes(customerId);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const { isDialogOpen, handleOpen: openDeleteDialog, handleClose: closeDeleteDialog } = useDialog();

  const createCustomerNoteMutation = useCreateCustomerNote();
  const modifyCustomerNoteMutation = useModifyCustomerNote();
  const removeCustomerNoteMutation = useRemoveCustomerNote();

  const currentUserId = getUserId();

  const handleSave = (noteId: string, content: string) => {
    modifyCustomerNoteMutation.mutate(
      { noteId, customerId, content },
      {
        onSuccess: () => setEditingNoteId(null),
        onError: (error) => {
          if (isError(error)) {
            const [parsedError] = JSON.parse(error.message);
            const errorMessage = parsedError.message;

            userMessage('error', errorMessage);
          }
        },
      },
    );
  };

  const handleDeleteClick = (noteId: string) => {
    setDeletingNoteId(noteId);
    openDeleteDialog();
  };

  const handleDeleteConfirm = () => {
    if (deletingNoteId) {
      removeCustomerNoteMutation.mutate(
        { noteId: deletingNoteId, customerId },
        {
          onSettled: () => {
            closeDeleteDialog();
            setDeletingNoteId(null);
          },
          onError: () => {
            userMessage('error', 'Unable to delete note');
          },
        },
      );
    }
  };

  const handleDeleteCancel = () => {
    closeDeleteDialog();
    setDeletingNoteId(null);
  };

  return (
    <VyPaper>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          marginBottom: "30px"
        }}>
        Notes & Remarks
      </Typography>
      <Stack divider={isNotEmptyArray(data?.notes) ? <VyDivider /> : undefined} sx={{
        gap: 2
      }}>
        <CreateCustomerNoteForm
          isCustomerNoteLoading={createCustomerNoteMutation.isLoading}
          onSubmit={async (formValues) => {
            await createCustomerNoteMutation.mutateAsync({ customerId, content: formValues.content });
          }}
        />

        <Stack sx={{
          gap: 2
        }}>
          {isLoading ? (
            <NotesListSkeleton />
          ) : (
            data?.notes.map(
              ({
                id,
                authorId,
                userEmail,
                content,
                authorFirstName,
                authorLastName,
                createdAt,
                updatedAt,
              }) => {
                const authorName = getAuthorName(
                  userEmail,
                  fromNullToUndefined(authorFirstName),
                  fromNullToUndefined(authorLastName),
                );
                const isCurrentUserAuthor = isNotNullOrUndefined(currentUserId) && authorId === currentUserId;
                const isNoteInEditMode = editingNoteId === id;
                const formattedCreatedAt = formatDate(vyDate(createdAt), 'MMM. D, YY HH:mm');

                const isNoteProcessing =
                  (modifyCustomerNoteMutation.isLoading &&
                    modifyCustomerNoteMutation.variables?.noteId === id) ||
                  (removeCustomerNoteMutation.isLoading &&
                    removeCustomerNoteMutation.variables?.noteId === id);

                return (
                  <VyNote
                    key={id}
                    id={id}
                    author={authorName}
                    content={content}
                    createdAt={formattedCreatedAt}
                    updatedAt={updatedAt}
                    canEdit={isCurrentUserAuthor}
                    toggleViewMode={() => setEditingNoteId(isNoteInEditMode ? null : id)}
                    isEditMode={isNoteInEditMode}
                    onSave={handleSave}
                    onDelete={() => handleDeleteClick(id)}
                    isLoading={isNoteProcessing}
                  />
                );
              },
            )
          )}
        </Stack>
      </Stack>
      <VyDialog
        open={isDialogOpen}
        onClose={handleDeleteCancel}
        label="Delete Note"
        labelDescription="Are you sure you want to delete this note?"
        warningText="This action is final and cannot be reversed"
        cancelButtonText="Cancel"
        ctaText="Delete"
        ctaButtonProps={{
          color: 'error',
          loading: removeCustomerNoteMutation.isLoading,
          loadingPosition: 'end',
        }}
        onSubmit={handleDeleteConfirm}
      />
    </VyPaper>
  );
};

function NoteSkeletonItem() {
  return (
    <Stack
      sx={{
        gap: 1,
        padding: 2,
        backgroundColor: '#F9FAFB',
        borderRadius: 1
      }}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1
        }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Stack>
          <Skeleton variant="text" width={120} height={20} />
          <Skeleton variant="text" width={80} height={16} />
        </Stack>
      </Stack>
      <Skeleton variant="text" width="80%" height={20} />
      <Skeleton variant="text" width="60%" height={20} />
    </Stack>
  );
}

function NotesListSkeleton() {
  return (
    <>
      <NoteSkeletonItem />
      <NoteSkeletonItem />
      <NoteSkeletonItem />
    </>
  );
}

function getAuthorName(email: string, firstName?: string, lastName?: string) {
  if (isNotNullOrUndefined(firstName) && isNotNullOrUndefined(lastName)) {
    return `${firstName} ${lastName}`;
  }

  return email;
}
